// ETAPA 6 - Página da jornada

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/infra/supabase/client";
import { useTempleJourney } from "@/data/temples/useTempleJourney";
// Replaced local header with global PageHeader
import { PageHeader } from "@/ui/components/layout/PageHeader";
import { TempleProgressCard } from "@/ui/components/temples/TempleProgressCard";
import { TempleCard } from "@/ui/components/temples/TempleCard";
import { TempleWithProgress } from "@/types/temple.types";
import { Card, CardContent } from "@/ui/components/ui/card";
import { ArrowRight, Loader2, Mountain } from "lucide-react";
import { toast } from "@/ui/components/ui/use-toast";
import { useIsMobile } from "@/controllers/hooks/use-mobile";

export default function TempleJourney() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string>("");
  const isMobile = useIsMobile();

  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate("/login");
        return;
      }

      setUserId(user.id);
    };

    checkAuth();
  }, [navigate]);

  const { temples, stats, currentTemple, isLoading, isError, error } =
    useTempleJourney(userId);

  // Handler de ação no templo
  const handleTempleAction = (temple: TempleWithProgress) => {
    if (temple.isLocked) {
      toast({
        title: "Templo Bloqueado",
        description: "Complete o templo anterior para desbloquear este.",
        variant: "destructive",
      });
      return;
    }

    // Navegar para a página de posicionamento do templo
    navigate(`/templo/${temple.id}/posicionamento`);
  };

  // Funções auxiliares para calcular layout
  const calculateAlignment = (index: number, totalTemples: number): string => {
    const isFirst = index === 0;
    const isLast = index === totalTemples - 1;
    if (isFirst || isLast) return "center";
    return index % 2 === 1 ? "right" : "left";
  };

  const calculateDiagonalStyle = (alignment: string, nextAlignment: string) => {
    const goingRight =
      (alignment === "center" && nextAlignment === "right") ||
      (alignment === "left" && nextAlignment === "right") ||
      (alignment === "left" && nextAlignment === "center");

    const goingLeft =
      (alignment === "center" && nextAlignment === "left") ||
      (alignment === "right" && nextAlignment === "left") ||
      (alignment === "right" && nextAlignment === "center");

    if (goingRight) {
      return {
        diagonalClass: "right-0 origin-top-right -rotate-[30deg]",
        diagonalHeight: "60px",
      };
    }
    if (goingLeft) {
      return {
        diagonalClass: "left-0 origin-top-left rotate-[30deg]",
        diagonalHeight: "60px",
      };
    }
    return { diagonalClass: "", diagonalHeight: "32px" };
  };

  // Função principal para calcular layout
  const getTempleLayout = (
    index: number,
    totalTemples: number,
    nextIndex: number,
    isMobile: boolean,
  ) => {
    // No mobile, sempre centralizado
    if (isMobile) {
      return {
        alignment: "center",
        nextAlignment: "center",
        offsetClass: "",
        diagonalClass: "",
        diagonalHeight: "32px",
      };
    }

    // Desktop: layout em zigue-zague
    const alignment = calculateAlignment(index, totalTemples);
    const nextAlignment = calculateAlignment(nextIndex, totalTemples);

    let offsetClass = "";
    if (alignment === "right") {
      offsetClass = "ml-[15%]";
    } else if (alignment === "left") {
      offsetClass = "mr-[15%]";
    }

    const { diagonalClass, diagonalHeight } = calculateDiagonalStyle(
      alignment,
      nextAlignment,
    );

    return {
      alignment,
      nextAlignment,
      offsetClass,
      diagonalClass,
      diagonalHeight,
    };
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent-800" />
      </div>
    );
  }

  // Error
  if (isError) {
    return (
      <div className="min-h-screen bg-[#fafaf9] flex items-center justify-center p-4">
        <Card className="max-w-xl bg-card border shadow-figma-md">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive mb-4 font-semibold">
              Erro ao carregar templos
            </p>
            <p className="text-sm text-foreground-secondary">
              {error instanceof Error ? error.message : "Erro desconhecido"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-[#fafaf9]">
      {/* Header fixo - Global */}
      <PageHeader
        title="Jornada dos 7 Templos"
        Icon={Mountain}
        onBack={() => navigate("/dashboard")}
      />

      {/* Conteúdo */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Card de Progresso Geral */}
        {stats && <TempleProgressCard stats={stats} />}

        {/* Card do Templo Atual (se houver) */}
        {currentTemple && (
          <div className="bg-white rounded-xl border border-black/[0.06] shadow-[0_1px_3px_rgba(0,0,0,0.03)] overflow-hidden">
            <div className="h-[2px] bg-gradient-to-r from-accent-800 via-accent-600 to-accent-800/20" />
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] text-neutral-400 font-semibold uppercase tracking-wider">
                    Templo Atual
                  </p>
                  <h3 className="text-sm font-bold text-neutral-800 mt-0.5">
                    {currentTemple.name}
                  </h3>
                  <p className="text-xs text-accent-800 font-semibold mt-0.5 tabular-nums">
                    {currentTemple.progress?.progress_percentage || 0}% completo
                  </p>
                </div>
                <span className="text-[9px] font-bold text-accent-800 bg-accent-800/[0.07] px-2 py-[3px] rounded-[5px] uppercase tracking-[0.06em]">
                  Em progresso
                </span>
              </div>
              <div className="h-[5px] bg-black/[0.04] rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-accent-800 to-accent-600 rounded-full transition-all duration-500"
                  style={{
                    width: `${currentTemple.progress?.progress_percentage || 0}%`,
                  }}
                />
              </div>
              <button
                onClick={() =>
                  navigate(`/templo/${currentTemple.id}/posicionamento`)
                }
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-accent-800 to-accent-600 text-white text-[13px] font-semibold shadow-[0_1px_3px_rgba(195,150,74,0.25)] hover:shadow-[0_2px_8px_rgba(195,150,74,0.3)] transition-all"
              >
                Continuar
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Lista de Templos */}
        <div className="relative pb-12">
          {temples.map((temple, index) => {
            const layout = getTempleLayout(
              index,
              temples.length,
              index + 1,
              isMobile,
            );

            return (
              <div
                key={temple.id}
                className={`relative ${isMobile ? "mb-6" : "mb-16"} ${layout.offsetClass}`}
              >
                {/* Container do Card */}
                <div className="flex justify-center">
                  <div className="w-full max-w-md">
                    <TempleCard temple={temple} onAction={handleTempleAction} />
                  </div>
                </div>

                {/* Linhas conectoras tipo escada */}
                {index < temples.length - 1 && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 z-40">
                    {/* Linha conectora - diagonal ou vertical */}
                    {layout.alignment === layout.nextAlignment ? (
                      /* Linha vertical simples quando ambos estão no mesmo lado */
                      <div
                        className={`w-1 mx-auto rounded-full ${isMobile ? "h-8" : "h-20"}`}
                        style={{
                          backgroundColor: temple.isCompleted
                            ? "#22c55e"
                            : temple.isLocked
                              ? "#e5e7eb"
                              : "#d97706",
                        }}
                      />
                    ) : (
                      /* Linha diagonal conectando ao próximo */
                      <div className="relative h-20">
                        <div
                          className={`absolute top-0 w-1 h-full rounded-full ${layout.diagonalClass}`}
                          style={{
                            width: "4px",
                            height: layout.diagonalHeight,
                            backgroundColor: temple.isCompleted
                              ? "#22c55e"
                              : temple.isLocked
                                ? "#e5e7eb"
                                : "#d97706",
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
